import { SENTILINK_BADGE_LABELS } from "shared/pages/ApplicationSummary/Verifications/verificationsSentilink.utils";

/** API / deal-list vocabulary (matches backend SENTILINK_LIST_STATUS_*). */
export const SENTILINK_RISK_STATUS = {
  HIGH_RISK: "HIGH_RISK",
  NEEDS_REVIEW: "NEEDS_REVIEW",
  LOW_RISK: "LOW_RISK",
  ERROR: "ERROR",
  SKIPPED_NO_SSN: "SKIPPED_NO_SSN",
  NOT_STARTED: "NOT_STARTED",
  NOT_APPLICABLE: "NOT_APPLICABLE",
  /** Overlay-only in-progress pill; not a list-filter API status. */
  CHECKING: "CHECKING",
} as const;

export type SentilinkRiskStatus =
  (typeof SENTILINK_RISK_STATUS)[keyof typeof SENTILINK_RISK_STATUS];

/** Deal-table filter dropdown options (AC: five tiers). */
export const SENTILINK_RISK_STATUS_FILTER_OPTIONS: {
  key: SentilinkRiskStatus;
  label: string;
}[] = [
  { key: SENTILINK_RISK_STATUS.LOW_RISK, label: SENTILINK_BADGE_LABELS.LOW_RISK },
  {
    key: SENTILINK_RISK_STATUS.NEEDS_REVIEW,
    label: SENTILINK_BADGE_LABELS.NEEDS_REVIEW,
  },
  { key: SENTILINK_RISK_STATUS.HIGH_RISK, label: SENTILINK_BADGE_LABELS.HIGH_RISK },
  { key: SENTILINK_RISK_STATUS.ERROR, label: SENTILINK_BADGE_LABELS.ERROR },
  {
    key: SENTILINK_RISK_STATUS.NOT_STARTED,
    label: SENTILINK_BADGE_LABELS.NOT_STARTED,
  },
];

const SENTILINK_RISK_STATUS_FILTER_KEYS = new Set<SentilinkRiskStatus>(
  SENTILINK_RISK_STATUS_FILTER_OPTIONS.map((option) => option.key)
);

/** Drop tiers removed from the filter dropdown (e.g. legacy session storage). */
export const sanitizeSentilinkRiskStatusFilter = (
  statuses?: string[] | null
): SentilinkRiskStatus[] =>
  (statuses ?? []).filter((status): status is SentilinkRiskStatus =>
    SENTILINK_RISK_STATUS_FILTER_KEYS.has(status as SentilinkRiskStatus)
  );

const SENTILINK_ATTENTION_TIERS = new Set<SentilinkRiskStatus>([
  SENTILINK_RISK_STATUS.NEEDS_REVIEW,
  SENTILINK_RISK_STATUS.HIGH_RISK,
  SENTILINK_RISK_STATUS.ERROR,
]);

const SENTILINK_APPLICANT_CHIP_TIERS = new Set(SENTILINK_ATTENTION_TIERS);
SENTILINK_APPLICANT_CHIP_TIERS.add(SENTILINK_RISK_STATUS.LOW_RISK);
SENTILINK_APPLICANT_CHIP_TIERS.add(SENTILINK_RISK_STATUS.CHECKING);

const SENTILINK_LABELED_CHIP_TIERS = new Set(SENTILINK_ATTENTION_TIERS);
SENTILINK_LABELED_CHIP_TIERS.add(SENTILINK_RISK_STATUS.CHECKING);

export const isSentilinkLabeledChipTier = (
  tier?: string | null
): tier is SentilinkRiskStatus =>
  !!tier && SENTILINK_LABELED_CHIP_TIERS.has(tier as SentilinkRiskStatus);

export const isSentilinkAttentionTier = (
  tier?: string | null
): tier is SentilinkRiskStatus =>
  !!tier && SENTILINK_ATTENTION_TIERS.has(tier as SentilinkRiskStatus);

export const shouldShowSentilinkApplicantChip = (
  tier?: string | null
): boolean =>
  !!tier && SENTILINK_APPLICANT_CHIP_TIERS.has(tier as SentilinkRiskStatus);

export const SENTILINK_FILTER_EMPTY_MESSAGE =
  "No applications match these filters.";

export const getSentilinkRiskStatusLabel = (
  status?: string | null
): string => {
  if (status === SENTILINK_RISK_STATUS.CHECKING) {
    return "Checking";
  }
  if (!status) {
    return SENTILINK_BADGE_LABELS.NOT_STARTED;
  }

  const option = SENTILINK_RISK_STATUS_FILTER_OPTIONS.find(
    (item) => item.key === status
  );
  if (option?.label) {
    return option.label;
  }

  const badgeLabel =
    SENTILINK_BADGE_LABELS[status as keyof typeof SENTILINK_BADGE_LABELS];
  return badgeLabel ?? status.replace(/_/g, " ");
};

export const getSentilinkApplicantTooltip = (tier: string): string =>
  `Identity Risk: ${getSentilinkRiskStatusLabel(tier)}`;

export const getSentilinkApplicantChipLabel = (tier: string): string =>
  `Identity Risk · ${getSentilinkRiskStatusLabel(tier)}`;
